import React, { useEffect, useState } from 'react'
import { Card, Form, Input, Button, message, Typography } from 'antd'
import {
	getAgentAppRelease,
	updateAgentAppRelease,
} from 'api/agentApp/agentAppRelease'

const { Title } = Typography
const { TextArea } = Input

/**
 * DashCX agent app (com.whatcx.app) release config for system admins.
 * Mobile compares native installed version to appVersion and prompts on mismatch.
 */
const AgentAppRelease: React.FC = () => {
	const [form] = Form.useForm()
	const [loading, setLoading] = useState(false)
	const [fetching, setFetching] = useState(true)

	useEffect(() => {
		const fetch = async () => {
			setFetching(true)
			try {
				const data = await getAgentAppRelease()
				form.setFieldsValue({
					appVersion: data.appVersion ?? '',
					storeUrl: data.storeUrl ?? '',
					description: data.description ?? '',
				})
			} catch {
				message.error('Failed to load agent app release settings')
			} finally {
				setFetching(false)
			}
		}
		fetch()
	}, [form])

	const onFinish = async (values: {
		appVersion: string
		storeUrl: string
		description: string
	}) => {
		setLoading(true)
		try {
			await updateAgentAppRelease(values)
			message.success('Agent app release settings saved')
		} catch {
			message.error('Failed to save settings')
		} finally {
			setLoading(false)
		}
	}

	return (
		<>
			<Title level={4}>Agent App Release</Title>
			<p style={{ marginBottom: 24, color: '#666' }}>
				Configure the suggested DashCX mobile app version. When the backend version
				differs from the app&apos;s native installed version, users see a soft
				update dialog (Update / Later). Only flip this after the build is live on
				the store.
			</p>
			<Card loading={fetching}>
				<Form
					form={form}
					layout='vertical'
					onFinish={onFinish}
					style={{ maxWidth: 560 }}
				>
					<Form.Item
						name='appVersion'
						label='Suggested version'
						rules={[{ required: true, message: 'Enter version (e.g. 1.1.7)' }]}
					>
						<Input placeholder='e.g. 1.1.7' />
					</Form.Item>
					<Form.Item
						name='storeUrl'
						label='Store / download URL'
						rules={[{ required: true, message: 'Enter store URL' }]}
					>
						<Input placeholder='https://play.google.com/store/apps/details?id=com.whatcx.app' />
					</Form.Item>
					<Form.Item name='description' label='Release notes'>
						<TextArea rows={4} placeholder='What is new in this release' />
					</Form.Item>
					<Form.Item>
						<Button type='primary' htmlType='submit' loading={loading}>
							Save
						</Button>
					</Form.Item>
				</Form>
			</Card>
		</>
	)
}

export default AgentAppRelease
